// injectScripts.js

// Inject script and fire the tracking code
export function injectScripts(script_payloads = []) {
  // ✅ do nothing if no payloads yet
  if (!script_payloads.length) return () => {};

  // (1) Regex for <script ...>...</script>
  // --> To extract the attributes and content of the scripts
  // ------> Attributes of script: async, defer and so on...
  // ------> Content: <script> {{ content_is_here }} </script>
  const scriptRegex = /<script\b([^>]*)>([\s\S]*?)<\/script>/gm;

  // (2) Regex for <noscript>...</noscript>
  // --> To extract the inner content of noscript
  // ------> Example: <noscript> <img .../> </noscript>
  // ------> Example: <noscript> <iframe ...></iframe> </noscript>
  // ⚠️ Important: dynamically injected <noscript> is ignored by browsers
  //    (because JS is enabled). This is just for visibility in the DOM.
  const noscriptRegex = /<noscript\b[^>]*>([\s\S]*?)<\/noscript>/gm;

  // Track injected scripts/nodes for cleanup
  const injected_nodes = [];

  // Loop through all items
  script_payloads.forEach((payload) => {
    if (!payload.snippet) return;

    // 📝 Extract <script> blocks
    const scriptMatches = [...payload.snippet.matchAll(scriptRegex)];
    scriptMatches.forEach((match) => {
      const attrs = match[1];
      const content = match[2];
      const scriptEl = document.createElement("script");

      // ✅ Parse attributes into key/value
      if (attrs && attrs.trim()) {
        const attrRegex = /(\w[\w-]*)(?:=["']?([^"']*)["']?)?/g;
        let attrMatch;
        while ((attrMatch = attrRegex.exec(attrs)) !== null) {
          const attrName = attrMatch[1];
          const attrValue = attrMatch[2] ?? "";
          if (attrValue === "") {
            scriptEl.setAttribute(attrName, "");
          } else {
            scriptEl.setAttribute(attrName, attrValue);
          }
        }
      }

      // ✅ Skip non-executable types
      const typeAttr = (scriptEl.getAttribute("type") || "").toLowerCase();
      const isExecutableType =
        !typeAttr ||
        typeAttr === "text/javascript" ||
        typeAttr === "application/javascript" ||
        typeAttr === "module";
      if (!isExecutableType) return;

      // ✅ Inline script content
      if (content && content.trim()) {
        scriptEl.innerHTML = content;
      }

      // Default to head if target is not specified
      const target = payload.target || "head";

      // Log errors for external scripts
      if (scriptEl.src) {
        scriptEl.addEventListener("error", (e) => {
          console.warn("[ScriptInjector] failed to load", scriptEl.src, e);
        });
      }

      if (target === "body") {
        document.body.appendChild(scriptEl);
      } else {
        document.head.appendChild(scriptEl);
      }

      injected_nodes.push({ element: scriptEl, target });
    });

    // 📝 Extract <noscript> blocks
    const noscriptMatches = [...payload.snippet.matchAll(noscriptRegex)];
    noscriptMatches.forEach((match) => {
      const content = match[1];
      if (!content || !content.trim()) return;

      const target = payload.target || "head";
      const noscriptEl = document.createElement("noscript");

      // Parse content into DOM nodes
      const wrapper = document.createElement("div");
      // wrapper.textContent = content.trim();
      wrapper.innerHTML = content.trim();

      // ✅ Validation: check allowed elements by target
      // The <noscript> element can be used in both <head> and <body>. When used inside <head>, the <noscript> element could only contain <link>, <style>, and <meta> elements.
      // Reference: https://www.w3schools.com/tags/tag_noscript.asp
      if (target === "head") {
        const validForHead = [...wrapper.childNodes].every(
          (node) =>
            node.nodeType === 3 || // text/whitespace
            (node.nodeType === 1 &&
              ["LINK", "STYLE", "META"].includes(node.tagName)),
        );
        if (!validForHead) {
          console.warn(
            "[ScriptInjector] Skipping <noscript> in <head> (invalid children):",
            wrapper.innerHTML,
          );
          return; // do not inject invalid noscript
        }
      }

      // Move parsed children into <noscript>
      while (wrapper.firstChild) {
        noscriptEl.appendChild(wrapper.firstChild);
      }

      if (target === "body") {
        document.body.appendChild(noscriptEl);
      } else {
        document.head.appendChild(noscriptEl);
      }

      injected_nodes.push({ element: noscriptEl, target });
    });
  });

  // Cleanup function
  return () => {
    injected_nodes.forEach(({ element, target }) => {
      const parent = target === "body" ? document.body : document.head;
      if (parent.contains(element)) {
        parent.removeChild(element);
      }
    });
  };
}
